import { trim, get, isFunction } from 'lodash';
import {
  isEmptyVal,
  billingCycleIsMonthly,
  billingCycleIsAnnual,
  getValueByBillingCycle,
  billingCycleIsOneMonth,
} from '@/utils';
import { COUNTRY_CODE_INDIA, MRS_STATUS_TYPE_MAP, PlanDisabledStatusEnum } from '@/common/constants';
import { isInrCurrency } from '@/utils/currency';
import {
  ONLINE_NOT_SUPPORT_BILLING_CYCLE,
  SMB_BILLING_CYCLE_MAP,
} from '@/common/service/non-standard-billing-cycle-utils';

const countryOption = {
	us: 'US',
	jp:'JP',
	in:'IN',
	other: 'OTHER',
};

export const isValidUSZip = (zipCode = "") => {
	return /(^\d{5}$)/.test(trim(zipCode));
}

export const isValidJPZip = (zipCode = "") => {
	return /(^[0-9-]*$)/.test(trim(zipCode));
}

export const isValidINZip = (zipCode = "") => {
	return /(^\d{6}$)/.test(trim(zipCode));
}

const validZipWithCountry = {
	[countryOption.us]: {
		validFn: isValidUSZip,
		i18nKey: 'billing.invalid_zip',
	},
	[countryOption.jp]: {
		validFn: isValidJPZip,
		i18nKey: 'billing.invalid_jp_zip',
	},
	[countryOption.in]: {
		validFn: isValidINZip,
		i18nKey: 'billing.invalid_in_zip',
	},

};

export const validZipCode = (formValue = {}) => {
	const { country, zip } = formValue;
	const { validFn, i18nKey = "" } = get(validZipWithCountry, country, {});
	if (isFunction(validFn) && !validFn(zip)) {
		return i18nKey;
	}
}


export const emptyValidator = (rule, value, callback) => {
	if (!value || value.trim() === '') {
		callback(new Error(rule.message));
	} else {
		callback();
	}
};

const emailRestrictedCheck = email => {
  let restrictedListChars = [];
  try {
    restrictedListChars = JSON.parse(window.newBillingConfig?.restrictedEmailSymbol) || [];
  } catch (e) {
    console.error(e);
  }
  return email && email.split('').every(char => restrictedListChars.every(item => !item.includes(char)));
};
export const isEmail = (email) => {
  const regCheck = /^([A-Za-z0-9_\-.^*+=`~|{}?\\/'\u4e00-\u9fa5])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,8})$/.test(email) && !(email.includes('.@') || email.startsWith('.'));
  return regCheck && emailRestrictedCheck(email);
};

export const emailValidator = (rule, email, callback) => {
  // if empty, should not show this error
  if (!email || email.trim() === "") {
    callback();
  } else {
    if (!isEmail(email)) {
      callback(new Error(rule.message));
    } else {
      callback();
    }
  }
}

const isEduEmail = email => {
  const regCheck = /^([A-Za-z0-9_\-.^*+=`~|{}?\\/'\u4e00-\u9fa5])+@([A-Za-z0-9_\-.])+\.edu$/.test(email) && !(email.includes('.@') || email.startsWith('.'));
  return regCheck && emailRestrictedCheck(email);
};

export const eduEmailValidator = (rule, email, callback, emailRegList = []) => {
  // if empty, should not show this error
  if (!email || email.trim() === "") {
    callback();
  } else {
    if (emailRegList.length) {
      if (!emailRegList.find(item => item.test(email))) {
        callback(new Error(rule.message));
      } else {
        callback();
      }
    } else if (!isEduEmail(email)) {
      callback(new Error(rule.message));
    } else {
      callback();
    }
  }
};


export const checkMRSChange = (payload = {}) => {
  try {
    const { oldPlan = {}, nextPlan = {}, nextPlanQuantity } = payload;

    const oldPlanCode = get(oldPlan, 'plan_code', '');
    const oldPlanAmount = get(oldPlan, 'amount', 0);
    const oldPlanLicense = get(oldPlan, 'cartInfo.selected_number', 0);
    const oldPlanBillingCycle = get(oldPlan, 'billing_cycle', '');
    const nextPlanCode = get(nextPlan, 'plan_code', '');
    const nextPlanAmount = get(nextPlan, 'amount', 0);
    const nextPlanLicense = isEmptyVal(nextPlanQuantity) ? get(nextPlan, 'cartInfo.selected_number', 0) : nextPlanQuantity;
    const nextPlanBillingCycle = get(nextPlan, 'billing_cycle', '');

    const isCurrentPlanUpgrade = oldPlanCode === nextPlanCode && nextPlanLicense >= oldPlanLicense;
    const isMonthlyToYearlyUpgradeCase = checkIsMonthlyToYearlyUpgradeCase(oldPlanLicense, oldPlanBillingCycle, nextPlanLicense, nextPlanBillingCycle);
    const isSMBUpgradeCase = checkIsSMBUpgradeCase(oldPlanLicense, oldPlanBillingCycle, nextPlanLicense, nextPlanBillingCycle);

    if (isCurrentPlanUpgrade || isMonthlyToYearlyUpgradeCase || isSMBUpgradeCase) {
      return MRS_STATUS_TYPE_MAP.CHANGE_NOW;
    } else {
      const oldPlanMMR = oldPlanLicense * oldPlanAmount / getValueByBillingCycle(oldPlanBillingCycle);
      const nextPlanMMR = nextPlanLicense * nextPlanAmount / getValueByBillingCycle(nextPlanBillingCycle);

      if (oldPlanMMR > nextPlanMMR) {
        return MRS_STATUS_TYPE_MAP.CHANGE_FUTURE;
      }
    }

    return MRS_STATUS_TYPE_MAP.CHANGE_NOW;
  } catch (e) {
    console.error(e);
    return MRS_STATUS_TYPE_MAP.CHANGE_NOW;
  }
};

export const checkIsMonthlyToYearlyUpgradeCase = (oldPlanLicense, oldPlanBillingCycle, nextPlanLicense, nextPlanBillingCycle) => {
  return nextPlanLicense >= oldPlanLicense
    && (billingCycleIsMonthly(oldPlanBillingCycle) || billingCycleIsOneMonth(oldPlanBillingCycle))
    && billingCycleIsAnnual(nextPlanBillingCycle);
};

export const checkIsSMBUpgradeCase = (oldPlanLicense, oldPlanBillingCycle, nextPlanLicense, nextPlanBillingCycle) => {
  return (ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(oldPlanBillingCycle) || ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(nextPlanBillingCycle))
    && nextPlanLicense >= oldPlanLicense
    && SMB_BILLING_CYCLE_MAP[nextPlanBillingCycle].index > SMB_BILLING_CYCLE_MAP[oldPlanBillingCycle].index;
};

export const inrCountryValidator = (rule, value, callback) => {
  if (value && isInrCurrency() && value !== COUNTRY_CODE_INDIA) {
    callback(new Error(rule.message));
  } else {
    callback();
  }
};

export const planCodeMapSubscriptionDisabled = (planList) => {
	return planList.reduce((acc, x) => {
    Object.keys(PlanDisabledStatusEnum).some(status => {
      if (x[status]) {
        acc[x.plan_code] = status
      }
      return x[status]
    })
		return acc;
	}, {});
}
